import { useEffect, useMemo, useState } from "react";
import { Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, brl, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export const repriceCandidates = (doc) => (doc.lines || []).filter((l) => l.kind === "product" && l.action === "existing" && l.old_cost != null && Math.abs(l.old_cost - l.unit_cost) > 0.0001);
const round99 = (x) => Math.max(0.99, Math.ceil(Math.round(x * 100) / 100) - 0.01);
const pctTxt = (a, b) => `${b >= a ? "+" : ""}${(((b - a) / a) * 100).toFixed(1).replace(".", ",")}%`;

function suggest(r, mode, pct, r99) {
  const base = mode === "keep" ? (r.old_cost > 0 && r.old_price > 0 ? r.cost * (r.old_price / r.old_cost) : null) : pct !== "" ? r.cost * (1 + Number(pct) / 100) : null;
  if (!(base > 0)) return null;
  return r99 ? round99(base) : Math.round(base * 100) / 100;
}

function ModeBar({ mode, setMode, pct, setPct, r99, setR99 }) {
  const btn = (k, l) => <button type="button" onClick={() => setMode(k)} data-testid={`reprice-mode-${k}`} className={cn("rounded-md px-3 py-1.5 text-sm transition-colors", mode === k ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{l}</button>;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
      <div className="flex gap-1 rounded-lg border p-1">{btn("keep", "Manter a margem de cada produto")}{btn("fixed", "Margem única")}</div>
      {mode === "fixed" && <label className="flex items-center gap-2 text-sm">Margem sobre o custo<Input type="number" min="0" step="any" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="ex.: 40" className="w-24" data-testid="reprice-margin-input" />%</label>}
      <label className="ml-auto flex items-center gap-2 text-sm">Terminar em ,99<Switch checked={r99} onCheckedChange={setR99} data-testid="reprice-round-switch" /></label>
    </div>
  );
}

function RepriceRow({ r, idx, sug, onChange }) {
  const up = r.cost > r.old_cost;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-3" data-testid={`reprice-row-${idx}`}>
      <Checkbox checked={r.apply} onCheckedChange={(v) => onChange({ apply: !!v })} data-testid={`reprice-apply-${idx}`} />
      <div className="min-w-0 flex-1 basis-[calc(100%-2.5rem)] sm:basis-0">
        <p className="line-clamp-2 text-sm font-medium">{r.name}</p>
        <p className={cn("flex flex-wrap items-center gap-1 font-mono text-xs", up ? "text-red-500" : "text-emerald-600 dark:text-emerald-400")}><Icon className="h-3.5 w-3.5" />Custo {brl(r.old_cost)} → {brl(r.cost)} ({pctTxt(r.old_cost, r.cost)})</p>
      </div>
      <div className="text-right text-xs text-muted-foreground">Preço atual<p className="font-mono text-sm text-foreground">{brl(r.price)}</p></div>
      <div className="w-28"><p className="text-xs text-muted-foreground">Novo preço</p>
        <Input type="number" step="0.01" min="0" value={r.custom !== "" ? r.custom : sug ?? ""} placeholder="—" onChange={(e) => onChange({ custom: e.target.value, apply: true })} className="h-9 font-mono" data-testid={`reprice-price-${idx}`} /></div>
    </div>
  );
}

export function RepriceDialog({ doc, onDone }) {
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState("keep");
  const [pct, setPct] = useState("");
  const [r99, setR99] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const lines = repriceCandidates(doc);
    api.post("/products/labels", { product_ids: lines.map((l) => l.item_id) }).then(({ data }) => {
      const cur = Object.fromEntries(data.map((p) => [p.id, p]));
      setRows(lines.filter((l) => cur[l.item_id]).map((l) => ({ id: l.item_id, name: cur[l.item_id].name, old_cost: l.old_cost, cost: l.unit_cost, old_price: l.old_price, price: cur[l.item_id].price, custom: "", apply: true })));
    }).catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [doc]);
  const sugs = useMemo(() => (rows || []).map((r) => suggest(r, mode, pct, r99)), [rows, mode, pct, r99]);
  const finalPrice = (r, i) => Number(r.custom !== "" ? r.custom : sugs[i]);
  const chosen = (rows || []).map((r, i) => ({ id: r.id, price: finalPrice(r, i), apply: r.apply })).filter((x) => x.apply && x.price > 0);
  const patch = (i) => (p) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const changeMode = (fn) => (v) => { fn(v); setRows((rs) => rs.map((x) => ({ ...x, custom: "" }))); };
  const apply = async () => {
    setBusy(true);
    try { await api.post("/products/reprice", { items: chosen.map(({ id, price }) => ({ id, price })) }); toast.success(`${chosen.length} preço(s) atualizado(s)`); onDone(); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onDone()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="reprice-dialog">
        <DialogHeader><DialogTitle>Remarcação de preços · NF-e {doc.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Estes produtos tiveram o custo alterado pela nota. Confira o preço sugerido, ajuste se quiser e aplique.</p>
        {!rows ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <div className="space-y-3">
            <ModeBar mode={mode} setMode={changeMode(setMode)} pct={pct} setPct={changeMode(setPct)} r99={r99} setR99={changeMode(setR99)} />
            {mode === "keep" && sugs.some((s) => s == null) && <p className="text-xs text-amber-600 dark:text-amber-400">Alguns produtos não tinham custo antes da nota: informe o novo preço manualmente ou use a margem única.</p>}
            <div className="divide-y rounded-lg border">{rows.map((r, i) => <RepriceRow key={r.id} r={r} idx={i} sug={sugs[i]} onChange={patch(i)} />)}</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onDone} disabled={busy} data-testid="reprice-skip-btn">Pular</Button>
          <Button onClick={apply} disabled={busy || !chosen.length} data-testid="reprice-apply-btn">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Aplicar {chosen.length} preço(s)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
